/**
 * `duro` — the default design system. Its `defaultTokens` are the base; the
 * Brand-driven groups (colors, spacing, radii, font families + size scale) are
 * regenerated by `generate()`, while shadows / weights / line-heights are Duro's
 * own taste and pass through.
 *
 * NOTE: values here are a reasonable Duro-aligned starting point; when wiring the
 * real Duro token contract, replace `defaultTokens` via the adapter (keep the
 * shape) — see docs/plans/2026-06-26-design-system-theme-token-designer.md.
 */
import type { DesignSystem } from "../designSystem.js";
import { registerDesignSystem } from "../designSystem.js";
import type { Tokens } from "../tokens.js";
import type { BrandInputs } from "../brand.js";

const defaultBrand: BrandInputs = {
  brandColor: "#4f46e5",
  accentColor: "#0ea5e9",
  headingFont: "Inter",
  bodyFont: "Inter",
  cornerRoundness: "rounded",
  density: "comfortable",
};

// Base tokens. The color scales / semantic roles below are placeholders that
// `generate()` overwrites; shadow / fontWeight / lineHeight / border pass through.
const flat = (v: string) =>
  ({
    "50": v,
    "100": v,
    "200": v,
    "300": v,
    "400": v,
    "500": v,
    "600": v,
    "700": v,
    "800": v,
    "900": v,
  }) as const;

const defaultTokens: Tokens = {
  color: {
    primary: flat("#4f46e5"),
    neutral: flat("#737373"),
    background: "#ffffff",
    foreground: "#111111",
    primarySolid: "#4f46e5",
    primaryForeground: "#ffffff",
    accent: "#0ea5e9",
    accentForeground: "#ffffff",
    muted: "#f5f5f5",
    mutedForeground: "#737373",
    border: "#e5e5e5",
    ring: "#4f46e5",
  },
  space: { xs: 4, sm: 8, md: 16, lg: 24, xl: 32, "2xl": 48, "3xl": 64 },
  radius: { none: 0, sm: 4, md: 8, lg: 16, full: 9999 },
  typography: {
    fontFamily: {
      heading: "Inter",
      body: "Inter",
      mono: "ui-monospace, monospace",
    },
    fontSize: {
      xs: 12,
      sm: 14,
      base: 16,
      lg: 18,
      xl: 20,
      "2xl": 24,
      "3xl": 30,
      "4xl": 36,
    },
    fontWeight: { normal: 400, medium: 500, semibold: 600, bold: 700 },
    lineHeight: { tight: 1.2, normal: 1.5, relaxed: 1.75 },
  },
  shadow: {
    sm: "0 1px 2px 0 rgba(0,0,0,0.05)",
    md: "0 4px 6px -1px rgba(0,0,0,0.1)",
    lg: "0 10px 15px -3px rgba(0,0,0,0.1)",
  },
  border: { thin: 1, thick: 2 },
};

export const duro: DesignSystem = {
  id: "duro",
  name: "Duro",
  defaultBrand,
  defaultTokens,
};

registerDesignSystem(duro);
